import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Controller, useForm } from 'react-hook-form'

import { View } from '../../atoms/View'

import { GenericErrorBox } from '../../molecules/GenericErrorBox'
import TemporaryWarningBox from '../../molecules/TemporaryWarningBox'
import GenericWarningBox from '../../molecules/GenericWarningBox'

import { EmailLoginTemplate } from '../../templates/EmailLoginTemplate'
import { EMAIL_LOGIN_THEMES } from '../../templates/EmailLoginTemplate/EmailLoginThemes'

import Lurker from '../../../util/lurker'
import Tracker from '../../../util/tracker'
import behaviors from '../../../util/behaviors'
import transientErrorsStorage from '../../../util/transientErrorsStorage'
import storage from '../../../util/storage'
import SmartLock from '../../../util/smartLock'
import { isValidEmail, isValidIdentifier } from '../../../util/validators'
import { getUnmaskedDocument } from '../../../util/document'

import passwordlessSendEmailApiCall from '../../../api/passwordlessSendEmailApiCall'

import { checkMail } from '../../../api/loginApiCall'
import httpStatus from 'http-status'
import { getModalLoginExtraData, isCheckIdentifierExperimentOn } from '../../../util/experiments'
import { LURKER_ACCOUNT_STATUS } from './SplitLoginEmailPage.consts'

const lurker = new Lurker('login_email')
const tracker = new Tracker('login_email')
const smartLock = new SmartLock('login')

const ErrorMsgWrapper = styled(View)`
  margin-top: 16px;
  margin-right: 0;
  margin-bottom: 16px;
  margin-left: 0;
`

const InstabilityBox = styled(GenericWarningBox)`
  border-style: none;
  border-radius: 8px;
  margin: 15px 0px 10px 0px;
  padding: 16px 24px;
  background: #fff7e0;
`
const InstabilityBoxFade = styled(View)`
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  div {
    animation-name: fadeIn;
    animation-duration: 1s;
  }
`

const apiErrors = {
  Unauthorized: 'showUnauthorizedError',
  Inactive: 'showInactiveError',
  Generic: 'showGenericError',
  RateLimit: 'showRateLimitError',
  AccountNotFound: 'showAccountNotFoundError',
  GoogleInitialization: 'showGoogleInitializationError',
  ExpiredToken: 'showExpiredTokenError',
  RedScore: 'showRedScoreError',
  AccountLocked: 'showAccountLockedError',
  AccountDisabledByFraud: 'showAccountDisabledByFraudError',
  Instability: 'showInstabilityError',
  NoNetwork: 'showNetworkError',
  Blocked: 'showBlockedError',
}

const errorMessages = {
  INVALID_EMAIL: 'Informe um e-mail válido.',
  INVALID_IDENTIFIER: 'Informe um e-mail, CPF ou CNPJ válido.',
}

const invalidEmails = []

export const SplitLoginEmailPage = ({
  facebookAppId,
  onChallengePinCodeCall,
  onError,
  onForgotPassword,
  onLoginSuccess,
  onMagicLinkClick,
  onMagicLinkEmailSentClick,
  onReactivationLinkClick,
  onRegisterClick,
  onRequireEmail,
  onSubmit,
  showReactivationLink,
  showHelpingModalLink,
  smartLockLogin,
  onPhoneNotValidatedError,
  abTests = '',
  sessionTokens,
  onAccountNotValidated,
  onAccountMustValidate,
  modalMode,
  onClickNeedHelp,
  onAccountSocialLoginTyping,
  onAccountCompleteData,
  themeName = 'DEFAULT',
}) => {
  const [currentError, setCurrentError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    control,
    formState: { isValid },
    handleSubmit,
    setValue,
    getValues,
    watch,
  } = useForm({
    defaultValues: { email: '' },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })

  const emailValue = watch('email')

  const onReactivationClick = () => {
    lurker.clickEvt('reactivation_link')
    if (isValid && typeof onReactivationLinkClick === 'function') {
      onReactivationLinkClick(getValues('email'))
    }
  }

  const onSendQuickAccess = () => event => {
    event.preventDefault()
    lurker.clickEvt('rate-limit-magiclink')
    if (isValid && typeof sendMagicLink === 'function') {
      sendMagicLink()
    }
  }

  const onLinkSended = () => {
    setCurrentError('')
    if (typeof onMagicLinkEmailSentClick === 'function') {
      onMagicLinkEmailSentClick(getValues('email'))
    }
  }

  const sendMagicLink = () => {
    getValues('email')
    setIsSubmitting(true)
    passwordlessSendEmailApiCall(getValues('email'))
      .then(onLinkSended)
      .catch(onEmailLoginError)
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const onEmailSubmit = ({ email }) => {
    const extraData = getModalLoginExtraData(abTests, modalMode)

    lurker.clickEvt('email', extraData)
    setIsSubmitting(true)
    checkMail(email)
      .then(response => {
        setIsSubmitting(false)
        onSubmit(email, response.data)
      })
      .catch(error => {
        onEmailLoginError(error)
        setIsSubmitting(false)
      })
  }

  const onOauthSuccess = data => {
    if (onLoginSuccess) {
      onLoginSuccess(data)
    }
    setIsSubmitting(false)
  }

  const onOauthError = error => {
    if (onError) {
      onError(error)
    }
    let errorBox = apiErrors.Generic
    if (error === 'google_initialization_error') {
      errorBox = apiErrors.GoogleInitialization
    }
    setCurrentError(errorBox)
    setIsSubmitting(false)
  }

  const onOauthCancel = () => {
    setIsSubmitting(false)
  }

  const onOauthSubmit = () => {
    setIsSubmitting(true)
  }

  const getErrorAlertbox = error => {
    const errorMessages = {
      [apiErrors.Instability]: (
        <InstabilityBoxFade>
          <InstabilityBox>
            <span className="typo-body-medium text-feedback-attention-110">
              Podemos apresentar instabilidades no login neste momento. Já estamos trabalhando para
              resolvê-las!
            </span>
          </InstabilityBox>
        </InstabilityBoxFade>
      ),
      [apiErrors.Generic]: (
        <ErrorMsgWrapper>
          <GenericErrorBox text="Não foi possível acessar sua conta. Por favor, tente novamente." />
        </ErrorMsgWrapper>
      ),
      [apiErrors.GoogleInitialization]: (
        <ErrorMsgWrapper>
          <GenericErrorBox
            showGoogleInitializationError
            text="Não foi possível entrar com o Google."
          />
        </ErrorMsgWrapper>
      ),
      [apiErrors.RateLimit]: (
        <ErrorMsgWrapper>
          <GenericErrorBox
            showContactUs={false}
            errorAction={onSendQuickAccess}
            showRateLimitMagicLink
            text="Você excedeu o número de tentativas."
          />
        </ErrorMsgWrapper>
      ),
      [apiErrors.Inactive]: (
        <ErrorMsgWrapper>
          <GenericErrorBox
            contactUsLink="https://ajuda.olx.com.br/s/article/reativar-conta-na-olx"
            showInactiveError={showReactivationLink}
            errorAction={showReactivationLink ? onReactivationClick : null}
            text="Esta conta foi desativada."
          />
        </ErrorMsgWrapper>
      ),
      [apiErrors.AccountDisabledByFraud]: (
        <ErrorMsgWrapper>
          <GenericErrorBox text="Conta temporariamente bloqueada." showContactUs />
        </ErrorMsgWrapper>
      ),
      [apiErrors.AccountNotFound]: (
        <ErrorMsgWrapper>
          <GenericErrorBox
            showAccountNotFoundError
            errorAction={onRegisterLinkClick}
            text="Não existe uma conta com este e-mail."
          />
        </ErrorMsgWrapper>
      ),
      [apiErrors.ExpiredToken]: (
        <ErrorMsgWrapper>
          <GenericErrorBox showContactUs={false} text="O link para acesso rápido expirou." />
        </ErrorMsgWrapper>
      ),
      [apiErrors.AccountLocked]: (
        <ErrorMsgWrapper>
          <TemporaryWarningBox
            title="Por favor, aguarde!"
            text="Estamos atualizando suas informações de contato.
                Um e-mail será enviado assim que o acesso for liberado."
          />
        </ErrorMsgWrapper>
      ),
      [apiErrors.NoNetwork]: (
        <ErrorMsgWrapper>
          <GenericErrorBox
            showContactUs={false}
            text="Não foi possível acessar sua conta."
            info="Por favor, verifique sua conexão com a internet e tente novamente."
          />
        </ErrorMsgWrapper>
      ),
      [apiErrors.Blocked]: (
        <ErrorMsgWrapper>
          <GenericErrorBox
            showContactUs={false}
            text="Houve um problema ao acessar sua conta."
            info="Por favor, verifique se há algum bloqueio na sua rede de internet."
          />
        </ErrorMsgWrapper>
      ),
    }

    return errorMessages[error] || errorMessages[apiErrors.Generic]
  }

  const onUnauthorizedError = (response, isSmartLock) => {
    const prefix = isSmartLock ? 'smart_lock_' : ''
    const { message = 'UNAUTHORIZED' } = response.data || {}
    const extraData = getModalLoginExtraData(abTests, modalMode)

    if (message === 'PIN_CODE') {
      if (typeof onChallengePinCodeCall === 'function') {
        onChallengePinCodeCall(response.data)
        lurker.redirectEvt('pin_code', {
          type: response.data.type,
        })
      }
    }
    if (message === 'ACCOUNT_NOT_FOUND') {
      setCurrentError(apiErrors.AccountNotFound)
      lurker.viewMessageEvt('account_status', {
        reason: `${prefix}${message.toLowerCase()}`,
        accounts_status_code: response.status,
        status: LURKER_ACCOUNT_STATUS.ACCOUNT_NOT_FOUND,
        ...extraData,
      })
    } else {
      lurker.viewMessageEvt('error', {
        reason: `${prefix}${message.toLowerCase()}`,
        accounts_status_code: response.status,
        ...extraData,
      })
      setCurrentError(apiErrors.Unauthorized)
    }
    if (isSmartLock) {
      smartLock.removeCredential(getValues('email'))
    }
  }

  const handleUnexpectedError = (error, prefix) => {
    const extraData = getModalLoginExtraData(abTests, modalMode)

    if (error?.message?.includes('timeout')) {
      setCurrentError(apiErrors.NoNetwork)
    }
    if (error?.response?.status) {
      lurker.viewMessageEvt('error', {
        reason: `${prefix}unexpected_status_code`,
        accounts_status_code: error?.response.status,
        ...extraData,
      })
      setCurrentError(apiErrors.Generic)
    } else {
      lurker.viewMessageEvt('error', {
        reason: `${prefix}unexpected_error`,
        accounts_error_message: error?.message || 'unknown',
        ...extraData,
      })
      setCurrentError(apiErrors.Generic)
    }
  }

  const onApiError = (error = {}, isSmartLock) => {
    const { response = {} } = error

    const prefix = isSmartLock ? 'smart_lock_' : ''
    const extraData = getModalLoginExtraData(abTests, modalMode)

    const errorActions = {
      'Network Error': () => {
        lurker.viewMessageEvt('error_blocked_cloudflare', { ...extraData })
        setCurrentError(apiErrors.Blocked)
      },
      [httpStatus.UNAUTHORIZED]: () => {
        onUnauthorizedError(response, isSmartLock)
      },
      [httpStatus.LOCKED]: () => {
        lurker.viewMessageEvt('account_status', {
          reason: `${prefix}account_locked`,
          accounts_status_code: response.status,
          status: LURKER_ACCOUNT_STATUS.ACCOUNT_LOCKED,
          ...extraData,
        })
        setCurrentError(apiErrors.AccountLocked)
      },
      [httpStatus.TOO_MANY_REQUESTS]: () => {
        lurker.viewMessageEvt('error', {
          reason: 'rate_limit_exceeded',
          accounts_status_code: response.status,
          ...extraData,
        })
        setCurrentError(apiErrors.RateLimit)
      },
      [httpStatus.GONE]: () => {
        lurker.viewMessageEvt('account_status', {
          reason: `${prefix}account_inactive`,
          accounts_status_code: response.status,
          status: LURKER_ACCOUNT_STATUS.ACCOUNT_INACTIVE,
          ...extraData,
        })
        setCurrentError(apiErrors.Inactive)
        invalidEmails.push(getValues('email'))
      },
      [httpStatus.FORBIDDEN]: () => {
        const { message = 'TURNSTILE_INVALID_TOKEN' } = response.data || {}

        switch (message) {
          case 'ACCOUNT_DISABLED':
            lurker.viewMessageEvt('account_status', {
              reason: `${prefix}${message.toLowerCase()}`,
              accounts_status_code: response.status,
              status: LURKER_ACCOUNT_STATUS.ACCOUNT_ON_RED_ZONE,
              ...extraData,
            })
            setCurrentError(apiErrors.AccountDisabledByFraud)
            break
          default:
            lurker.viewMessageEvt('error', {
              reason: `${prefix}${message.toLowerCase()}`,
              accounts_status_code: response.status,
              ...extraData,
            })
            setCurrentError(apiErrors.Generic)
        }
      },
      [httpStatus.BAD_REQUEST]: () => {
        if (response.data && response.data.trim() === 'Missing turnstile token') {
          lurker.viewMessageEvt('error', {
            reason: `${prefix}turnstile_missing_token`,
            accounts_status_code: response.status,
            ...extraData,
          })
          setCurrentError(apiErrors.Generic)
        }
      },
      default: () => {
        handleUnexpectedError(response)
      },
    }

    const action =
      errorActions[response.status] || errorActions[error?.message] || errorActions.default
    return action()
  }

  const onEmailLoginError = (error = {}, isSmartLock) => {
    if (onError) {
      onError()
    }
    onApiError(error, isSmartLock)
    behaviors.scrollToTop()
  }

  const onHelpLinkClick = () => {
    lurker.clickEvt('help_modal_open')
  }

  const onHelpLinkClose = () => {
    lurker.clickEvt('help_modal_close')
  }

  const onRegisterLinkClick = () => {
    lurker.clickEvt('signup')

    if (onRegisterClick) {
      onRegisterClick()
    }
  }

  const checkStoredEmail = () => {
    // eslint-disable-next-line import/no-named-as-default-member
    storage.get('loggedInEmail').then(value => {
      if (typeof value === 'string') {
        setValue('email', value, { shouldValidate: true })
      }
    })
  }

  const checkTransientErrors = () => {
    transientErrorsStorage.get().then(error => {
      if (typeof error === 'string') {
        setCurrentError(error)
      }
    })
  }

  const trySmartLockLogin = () => {
    if (smartLockLogin) {
      smartLock.requestCredential().then(credentials => {
        if (credentials) {
          const { email } = credentials
          setValue('email', email, { shouldValidate: true })
        }
      })
    }
  }

  const onMailCheckSuggestionClick = suggestion => {
    lurker.viewMessageEvt('mailcheck_suggestion_accepted', {
      mailcheck_original: getValues('email'),
      mailcheck_suggestion: suggestion,
    })
    setValue('email', suggestion, { shouldValidate: true })
  }

  const onPrivacyLinkClick = () => {
    lurker.clickEvt('privacy_button')
  }

  const onTermsLinkClick = () => {
    lurker.clickEvt('terms_button')
  }

  useEffect(() => {
    if (modalMode) {
      lurker.pageLoadWithExtraDataEvt('login_email', { from_modal: true })
    } else {
      lurker.pageLoadEvt()
    }

    tracker.pageLoadEvt()
    checkStoredEmail()
    checkTransientErrors()
    trySmartLockLogin()
  }, [])

  return (
    <EmailLoginTemplate
      abTests={abTests}
      facebookAppId={facebookAppId}
      onRequireEmail={onRequireEmail}
      onMagicLinkClick={onMagicLinkClick}
      onSubmit={handleSubmit(onEmailSubmit)}
      onRegisterClick={onRegisterLinkClick}
      onHelpLinkClick={onHelpLinkClick}
      onForgotPassword={onForgotPassword}
      onReactivationLinkClick={onReactivationLinkClick}
      onHelpLinkClose={onHelpLinkClose}
      onMailCheckSuggestionClick={onMailCheckSuggestionClick}
      onOauthCancel={onOauthCancel}
      onOauthError={onOauthError}
      onOauthSubmit={onOauthSubmit}
      onOauthSuccess={onOauthSuccess}
      onPrivacyLinkClick={onPrivacyLinkClick}
      onTermsLinkClick={onTermsLinkClick}
      onPhoneNotValidatedError={onPhoneNotValidatedError}
      isSubmitting={isSubmitting}
      showHelpingModalLink={showHelpingModalLink}
      shouldDisableSubmit={isSubmitting || !emailValue?.trim()}
      errorMsg={currentError ? getErrorAlertbox(currentError) : null}
      onChallengePinCodeCall={onChallengePinCodeCall}
      lurker={lurker}
      tracker={tracker}
      themeName={themeName}
      inputWrapper={renderComponent => {
        const checkIdentifierEnabled = isCheckIdentifierExperimentOn(abTests)
        const validationError = checkIdentifierEnabled
          ? errorMessages['INVALID_IDENTIFIER']
          : errorMessages['INVALID_EMAIL']

        return (
          <Controller
            control={control}
            name="email"
            rules={{
              required: validationError,
              validate: email => {
                const isEmail = email?.includes('@')
                const unmaskedValue =
                  checkIdentifierEnabled && !isEmail ? getUnmaskedDocument(email) : email
                const isValid = checkIdentifierEnabled
                  ? isValidIdentifier(unmaskedValue)
                  : isValidEmail(unmaskedValue)
                const isNotInactive =
                  currentError !== apiErrors.Inactive || !invalidEmails.includes(email)
                return (isValid && isNotInactive) || validationError
              },
            }}
            render={renderComponent}
          />
        )
      }}
      sessionTokens={sessionTokens}
      onAccountNotValidated={onAccountNotValidated}
      onAccountMustValidate={onAccountMustValidate}
      modalMode={modalMode}
      onClickNeedHelp={onClickNeedHelp}
      onAccountSocialLoginTyping={onAccountSocialLoginTyping}
      onAccountCompleteData={onAccountCompleteData}
      theme={EMAIL_LOGIN_THEMES[themeName] || EMAIL_LOGIN_THEMES.DEFAULT}
    />
  )
}

SplitLoginEmailPage.propTypes = {
  abTests: PropTypes.string,
  emailStorage: PropTypes.bool,
  facebookAppId: PropTypes.string.isRequired,
  onChallengePinCodeCall: PropTypes.func,
  onError: PropTypes.func,
  onForgotPassword: PropTypes.func,
  onLoginSuccess: PropTypes.func,
  onMagicLinkEmailSentClick: PropTypes.func,
  onMagicLinkClick: PropTypes.func,
  onReactivationLinkClick: PropTypes.func,
  onRegisterClick: PropTypes.func,
  onRequireEmail: PropTypes.func,
  onSubmit: PropTypes.func,
  onPhoneNotValidatedError: PropTypes.func,
  showReactivationLink: PropTypes.bool,
  showHelpingModalLink: PropTypes.bool,
  smartLockLogin: PropTypes.bool,
  sessionTokens: PropTypes.object,
  onAccountNotValidated: PropTypes.func,
  modalMode: PropTypes.bool,
  onClickNeedHelp: PropTypes.func,
  onAccountSocialLoginTyping: PropTypes.func,
  onAccountCompleteData: PropTypes.func,
  themeName: PropTypes.oneOf(Object.keys(EMAIL_LOGIN_THEMES)),
}
